import React, { useState, useMemo, useEffect } from "react";
import { PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Input,
  Select,
  Col,
  Row,
  Popconfirm,
  Space,
  DatePicker,
  Radio,
  Tabs,
} from "antd";
import { ReactComponent as DeleteIcon } from "../../../../../assets/delete-icon.svg";
import { ReactComponent as SaveIcon } from "../../../../../assets/circular-save-btn.svg";
import { ReactComponent as CrossIcon } from "../../../../../assets/crossIcon.svg";
import moment from "moment";
import { materialFilterOptions, boltFilterOptions } from "../../../utils";
import { fetchProjectPriorities } from "../../../../ProjectSetup/redux/actions";
import { fetchSuppliers } from "../../../../supplier/redux/actions";
import { fetchCommodities } from "../../../../commodity/redux/actions";
import { useDispatch, useSelector } from "react-redux";

// Default status options
const statusOptions = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
  { label: "Pending", value: "pending" },
];
const { Option } = Select;

const DistributionFieldsComponent = ({
  inputFields,
  setInputFields,
  createField,
  deleteField,
  updateField,
  canUpdate,
  selectedTemplate,
  onTypeChange,
  project_id,
}) => {
  const dispatch = useDispatch();
  
  const { projectPriorities, fetchPrioritiesLoading } = useSelector((state) => state.projectsSetupReducer || {});
  const { allSuppliers, fetchingLoading: suppliersLoading } = useSelector((state) => state.supplierTypeReducer || {});
  const { allCommodities, fetchingLoading: commoditiesLoading } = useSelector((state) => state.commodityReducer || {});
  
  useEffect(() => {
    if (project_id) {
      dispatch(fetchProjectPriorities(project_id));
    }
    dispatch(fetchSuppliers({ page: 1, limit: 100, search: {} }));
    dispatch(fetchCommodities({ page: 1, limit: 100, search: {} }));
  }, [dispatch, project_id]);
  
  // Format options for dropdowns
  const dropdownOptions = useMemo(() => {
    return {
      originalPriorityId: projectPriorities.map(priority => ({
        label: priority.name,
        value: priority.id.toString()
      })),
      newPriorityId: projectPriorities.map(priority => ({
        label: priority.name,
        value: priority.id.toString()
      })),
      supplierId: allSuppliers.map(supplier => ({
        label: supplier.name,
        value: supplier.id.toString()
      })),
      commodityId: allCommodities.map(commodity => ({
        label: commodity.name,
        value: commodity.id.toString()
      })),
      status: statusOptions
    };
  }, [projectPriorities, allSuppliers, allCommodities]);
  
  const [selectedType, setSelectedType] = useState(null);
  
  // Get current options based on selected type
  const currentFilterOptions = useMemo(() => {
    if (!selectedType) return [];
    return selectedType === "material" ? materialFilterOptions : boltFilterOptions;
  }, [selectedType, materialFilterOptions, boltFilterOptions]);
  const handleDeleteLocally = (item) => {
    const filteredInputs = inputFields.filter((el) => el.id !== item.id);
    setInputFields(filteredInputs);
  };

  const addChooseField = () => {
    let field = {
      isEdit: true,
      name: "",
      isLocal: true,
      id: Math.floor(Math.random() * 10000000),
      label: "Select Field",
      placeholder: `Select ${selectedType === "material" ? "Material" : "Bolts"} Field`,
      isSelect: true,
    };
    setInputFields([...inputFields, { ...field }]);
  };

  const handleChangeField = (value, ele, option) => {
    const { index } = ele;

    let fields = [...inputFields];
    fields[index] = {
      ...ele,
      // id: value,
      value,
      option,
    };
    setInputFields(fields);
    
    // Automatically create the field when a value is selected
    createField({ ...fields[index], name: '' });
  };

  const handleDisable = (value) => {
    let item = inputFields.filter((el) => el.value === value && !el.isSelect);

    let shuoldDisabled = item.length > 0 ? true : false;

    return shuoldDisabled;
  };

  const SelectOption = ({ placeholder, ele }) => (
    <Select
      style={{ display: "block" }}
      placeholder={placeholder}
      size="small"
      getPopupContainer={() =>
        document.getElementsByClassName(
          "ant-layout-content layout-content site-layout-background custom-textarea"
        )[0]
      }
      disabled={!canUpdate}
      onChange={(e, option) => handleChangeField(e, ele, option)}
      value={ele?.value || null}
    >
      {currentFilterOptions.map((el) => {
        const { value, label } = el;
        return (
          <Option value={value} key={value} disabled={handleDisable(value)}>
            {label}
          </Option>
        );
      })}
    </Select>
  );

  const RenderFieldTypes = ({ item, localName, setLocalName }) => {
    const { placeholder, value, index } = item;

    // Handle date fields
    if (value === "distribution_date") {
      return (
        <DatePicker
          format={"DD MMM YYYY"}
          size="small"
          allowClear={false}
          suffixIcon={null}
          placeholder={"Select Date"}
          value={localName}
          disabled={!canUpdate}
          onChange={(e) => {
            setLocalName(moment(e));
            inputFields[index].name = moment(e);
            setInputFields(inputFields);
            updateField({ ...item, name: moment(e) });
          }}
        />
      );
    }
    
    // Handle dropdown fields
    if (["originalPriorityId", "newPriorityId", "supplierId", "commodityId", "status"].includes(value)) {
      // Check if options are available for this field
      const options = dropdownOptions[value] || [];
      
      return (
        <Select
          placeholder={`Select ${placeholder}`}
          size="small"
          value={localName}
          disabled={!canUpdate || options.length === 0}
          loading={options.length === 0} // Show loading state if options aren't loaded yet
          getPopupContainer={() =>
            document.getElementsByClassName(
              "ant-layout-content layout-content site-layout-background custom-textarea"
            )[0]
          }
          onChange={(selectedValue) => {
            setLocalName(selectedValue);
            inputFields[index].name = selectedValue;
            // Store the display label as well for better display
            const selectedOption = options.find(opt => opt.value === selectedValue);
            if (selectedOption) {
              inputFields[index].displayLabel = selectedOption.label;
            }
            setInputFields([...inputFields]);
            updateField({ ...item, name: selectedValue, displayLabel: selectedOption?.label });
          }}
        >
          {options.map(option => (
            <Option key={option.value} value={option.value}>
              {option.label}
            </Option>
          ))}
        </Select>
      );
    }

    // Default case: render a text input
    return (
      <Input
        placeholder={`${placeholder}`}
        value={localName}
        disabled={!canUpdate}
        onChange={(e) => {
          const { value } = e.target;
          setLocalName(value);
          inputFields[index].name = value;
          setInputFields(inputFields);
        }}
        onBlur={() => {
          updateField({ ...item, name: localName });
        }}
      />
    );
  };

  const RenderReadOnlyFields = ({ item }) => {
    let { value, name } = item;

    if (value === "distribution_date") {
      name = moment(name).format("DD MMM YYYY");
    }

    if (value === "revision_status_id") {
      name = item.selectOption.children;
    }
    
    // For dropdown fields, display the label instead of the value
    if (["originalPriorityId", "newPriorityId", "supplierId", "commodityId", "status"].includes(value)) {
      if (item.displayLabel) {
        name = item.displayLabel;
      } else {
        // Try to find the label for the selected value
        const options = dropdownOptions[value] || [];
        const selectedOption = options.find(opt => opt.value === name);
        if (selectedOption) {
          name = selectedOption.label;
        }
      }
    }

    return (
      <Input size="small" className="border-less-input" readOnly value={name} />
    );
  };

  const RenderInputs = ({ ele }) => {
    const { isEdit, label, placeholder, name, value } = ele;
    // For dropdown fields, use null instead of empty string to show placeholder
    const initialValue = ["originalPriorityId", "newPriorityId", "supplierId", "commodityId", "status"].includes(value) && !name ? null : name;
    const [localName, setLocalName] = useState(initialValue);

    return (
      <Row
        align="bottom"
        justify="start"
        className="field-row"
        gutter={[15, 0]}
      >
        <Col span={18}>
          <label>{label}</label>
          {ele?.isEdit ? (
            ele?.isSelect ? (
              <SelectOption placeholder={placeholder} ele={ele} />
            ) : (
              <RenderFieldTypes
                item={ele}
                localName={localName}
                setLocalName={setLocalName}
              />
            )
          ) : (
            <RenderReadOnlyFields item={ele} />
          )}
        </Col>
        {canUpdate && (
          <Col>
            {isEdit ? (
              <Space size={10} align="baseline">
                <Popconfirm
                  title={ele?.isSelect ? "Are you sure you want to cancel?" : "Are you sure you want to delete?"}
                  placement="topRight"
                  onConfirm={() => handleDeleteLocally(ele)}
                  okText="Yes"
                  cancelText="No"
                >
                  {ele?.isSelect ? (
                    <CrossIcon />
                  ) : (
                    <DeleteIcon width={16} height={16} fill="#e21c21" />
                  )}
                </Popconfirm>
              </Space>
            ) : (
              <Space size={10} align="baseline">
                {/* <EditIcon onClick={() => onEditClick(ele)} /> */}
                <Popconfirm
                  title="Are you sure you want to delete?"
                  placement="topRight"
                  onConfirm={() => deleteField(ele)}
                  okText="Yes"
                  cancelText="No"
                >
                  <DeleteIcon width={16} height={16} fill="#e21c21" />
                </Popconfirm>
              </Space>
            )}
          </Col>
        )}
      </Row>
    );
  };

  return (
          <Row gutter={[20, 0]} className="custom-fields-parent-div">
            <Col span={18} className="choose-col">
              <div className="choose-select-div">
                <label>{"TYPE"}</label>
                <Row>
                  <Select
                    placeholder="Select Type"
                    size="small"
                    getPopupContainer={() =>
                      document.getElementsByClassName(
                        "ant-layout-content layout-content site-layout-background custom-textarea"
                      )[0]
                    }
                    disabled={!canUpdate}
                    onChange={(value) => {
                      setSelectedType(value);
                      if (onTypeChange) {
                        onTypeChange(value);
                      }
                    }}
                    value={selectedType}
                  >
                    <Option value="material">Material</Option>
                    <Option value="bolts">Bolts</Option>
                  </Select>
                </Row>
              </div>
            </Col>
            {inputFields.map((el, i) => {
              return (
                <Col span={24} key={`item-${el.id}`} index={i}>
                  <RenderInputs ele={{ ...el, index: i }} />
                </Col>
              );
            })}
            {canUpdate && selectedType && (
              <Col span={24}>
                <Button
                  onClick={addChooseField}
                  icon={<PlusOutlined />}
                  size="small"
                  className="add-field-btn"
                >
        Filter
                </Button>
              </Col>
            )}
          </Row>
  );
};

export default DistributionFieldsComponent;
